import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { movies } from '@/mocks/movies';
import { news } from '@/mocks/news';

interface GlobalSearchProps {
  variant?: 'desktop' | 'mobile';
  onNavigate?: () => void;
}

export default function GlobalSearch({ variant = 'desktop', onNavigate }: GlobalSearchProps) {
  const [keyword, setKeyword] = useState('');
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const results = useMemo(() => {
    const kw = keyword.trim();
    if (!kw) return { movies: [], news: [] };
    return {
      movies: movies.filter((m) => m.title.includes(kw) || m.enTitle.toLowerCase().includes(kw.toLowerCase())).slice(0, 4),
      news: news.filter((n) => n.title.includes(kw)).slice(0, 4),
    };
  }, [keyword]);

  const hasResults = results.movies.length > 0 || results.news.length > 0;

  const go = (path: string) => {
    setOpen(false);
    setKeyword('');
    onNavigate?.();
    navigate(path);
  };

  const submit = () => {
    const kw = keyword.trim();
    if (!kw) return;
    go(`/movies?q=${encodeURIComponent(kw)}`);
  };

  return (
    <div ref={wrapRef} className="relative w-full">
      <div className="flex items-center gap-2 rounded-md border border-background-300 bg-background-100 px-3 h-10 focus-within:border-primary-400 transition-colors">
        <i className="ri-search-line text-foreground-400 text-base w-4 h-4 flex items-center justify-center"></i>
        <input
          type="text"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
          placeholder="搜索电影或资讯"
          className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
        />
        {keyword && (
          <button
            type="button"
            onClick={() => setKeyword('')}
            className="text-foreground-400 hover:text-foreground-700 cursor-pointer"
            aria-label="清空"
          >
            <i className="ri-close-line w-4 h-4 flex items-center justify-center"></i>
          </button>
        )}
      </div>

      {open && keyword.trim() && (
        <div
          className={`absolute z-50 mt-2 rounded-lg border border-background-200 bg-background-50 shadow-lg overflow-hidden ${
            variant === 'mobile' ? 'left-0 right-0' : 'right-0 w-[380px]'
          }`}
        >
          {!hasResults && (
            <p className="px-4 py-6 text-sm text-foreground-500 text-center">
              未找到与「{keyword}」相关的内容
            </p>
          )}

          {results.movies.length > 0 && (
            <div className="py-2">
              <p className="px-4 py-1 text-xs font-medium text-foreground-400">电影</p>
              {results.movies.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => go(`/movies/detail/${m.id}`)}
                  className="w-full flex items-center gap-3 px-4 py-2 hover:bg-background-100 text-left cursor-pointer"
                >
                  <span className="w-9 h-12 rounded overflow-hidden bg-foreground-100 shrink-0">
                    <img src={m.poster} alt={m.title} className="w-full h-full object-cover object-top" />
                  </span>
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-foreground-900 truncate">{m.title}</span>
                    <span className="block text-xs text-foreground-400 truncate">
                      {m.year} · {m.genres.join(' / ')}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          )}

          {results.news.length > 0 && (
            <div className="py-2 border-t border-background-200">
              <p className="px-4 py-1 text-xs font-medium text-foreground-400">资讯</p>
              {results.news.map((n) => (
                <button
                  key={n.id}
                  type="button"
                  onClick={() => go(`/news/detail/${n.id}`)}
                  className="w-full flex items-center gap-2 px-4 py-2 hover:bg-background-100 text-left cursor-pointer"
                >
                  <i className="ri-article-line text-foreground-400 w-4 h-4 flex items-center justify-center shrink-0"></i>
                  <span className="text-sm text-foreground-900 truncate">{n.title}</span>
                </button>
              ))}
            </div>
          )}

          {hasResults && (
            <Link
              to={`/movies?q=${encodeURIComponent(keyword.trim())}`}
              onClick={() => {
                setOpen(false);
                onNavigate?.();
              }}
              className="block px-4 py-2.5 text-sm text-primary-600 hover:bg-background-100 border-t border-background-200"
            >
              查看「{keyword.trim()}」的全部电影结果
              <i className="ri-arrow-right-line ml-1 w-4 h-4 inline-flex align-middle items-center justify-center"></i>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}